import { supabase } from '@/integrations/supabase/client';

// Types
interface HubspotContactData {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  hubspot_contact_id?: string;
  [key: string]: any;
}

interface HubspotResponse {
  success: boolean;
  status: number;
  data: any;
  error?: string;
}

/**
 * Creates or updates a contact in Hubspot and stores the contact ID in the user's profile
 * @param action The action to perform (create or update)
 * @param data The contact data
 * @param userId The user ID for storing the Hubspot contact ID in their profile
 * @returns The Hubspot API response
 */
export async function hubspotContactSync(
  action: 'create' | 'update',
  data: HubspotContactData,
  userId?: string
): Promise<HubspotResponse> {
  try {
    // Prepare the request body
    const requestBody = {
      action,
      data,
      userId
    };
    
    // Call our Supabase Edge Function
    const { data: response, error } = await supabase.functions.invoke('hubspot-api', {
      body: JSON.stringify(requestBody)
    });
    
    if (error) {
      return {
        success: false,
        status: 500,
        data: null,
        error: error.message || 'Unknown error from Edge Function'
      };
    }
    
    return response as HubspotResponse;
  } catch (error: any) {
    return {
      success: false,
      status: 500,
      data: null,
      error: error.message || 'Unknown error'
    };
  }
}

/**
 * Creates a contact in Hubspot and stores the contact ID in the user's profile
 * @param email Email address
 * @param firstName First name
 * @param lastName Last name
 * @param company Company name
 * @param userId User ID to store the Hubspot contact ID in their profile
 * @param skipVerification If true, will not auto-verify the user
 * @returns The Hubspot API response
 */
export async function createHubspotContact(
  email: string,
  firstName?: string,
  lastName?: string,
  company?: string,
  userId?: string,
  skipVerification = false
): Promise<HubspotResponse> {
  try {
    // Call with more detailed parameters
    const requestData = { email, firstName, lastName, company };
    
    // First attempt: Try creating the contact
    let response = await hubspotContactSync(
      'create',
      requestData,
      userId
    );
    
    // If there's an error with status 409 (Conflict), it means the contact already exists
    // In this case, try to update the contact instead
    if (!response.success && response.status === 409) {
      console.log('📧 Contact already exists in HubSpot, attempting to update instead:', email);
      
      // Try updating the contact since it already exists
      const updateResponse = await hubspotContactSync(
        'update',
        requestData,
        userId
      );
      
      // If update succeeds, use that response
      if (updateResponse.success) {
        console.log('✅ Successfully updated existing HubSpot contact:', email);
        response = updateResponse;
      } else {
        console.error('❌ Failed to update existing HubSpot contact:', updateResponse.error);
      }
    }
    
    // Update the user's profile only if we have a userId and not skipping verification
    if (userId && !skipVerification) {
      try {
        // First check if the user needs manual verification
        const { data: profile, error: profileCheckError } = await supabase
          .from('profiles')
          .select('needs_manual_verification')
          .eq('id', userId)
          .single();
          
        // Only auto-verify if the user doesn't need manual verification
        if (!profileCheckError && profile && profile.needs_manual_verification !== true) {
          const { error: updateError } = await supabase
            .from('profiles')
            .update({
              verified_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .eq('id', userId);
            
          if (updateError) {
            console.error('❌ Error updating profile verification status:', updateError);
          } else {
            console.log('✅ Profile verification status updated successfully');
            
            // If the HubSpot call failed but we verified the profile, return partial success
            if (!response.success) {
              response = {
                success: true, // Mark as success anyway
                status: 200,
                data: { partial_success: true, message: "Profile updated but HubSpot contact operation failed" },
                error: "HubSpot contact operation failed, but profile was updated for verification"
              };
            }
          }
        } else {
          console.log('👁️ User needs manual verification - skipping auto-verification in HubSpot flow');
        }
      } catch (profileError) {
        console.error('❌ Exception updating profile verification status:', profileError);
      }
    }
    
    return response;
  } catch (error) {
    console.error('❌ Exception in createHubspotContact:', error);
    
    // Update the profile anyway if we have a userId and not skipping verification
    if (userId && !skipVerification) {
      try {
        // First check if the user needs manual verification
        const { data: profile, error: profileCheckError } = await supabase
          .from('profiles')
          .select('needs_manual_verification')
          .eq('id', userId)
          .single();
          
        // Only auto-verify if the user doesn't need manual verification
        if (!profileCheckError && profile && profile.needs_manual_verification !== true) {
          const { error: updateError } = await supabase
            .from('profiles')
            .update({
              verified_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .eq('id', userId);
            
          if (!updateError) {
            // Return partial success
            return {
              success: true, 
              status: 200,
              data: { fallback_success: true, message: "Profile verified despite HubSpot error" },
              error: "HubSpot error but profile verification succeeded"
            };
          }
        }
      } catch (profileError) {
        console.error('❌ Exception in fallback profile update:', profileError);
      }
    }
    
    return {
      success: false,
      status: 500,
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Updates a contact in Hubspot
 * @param hubspotContactId The Hubspot contact ID
 * @param data The updated contact data
 * @returns The Hubspot API response
 */
export async function updateHubspotContact(
  hubspotContactId: string,
  data: Partial<Omit<HubspotContactData, 'email'>> & { email: string }
): Promise<HubspotResponse> {
  return hubspotContactSync(
    'update',
    { ...data, hubspot_contact_id: hubspotContactId }
  );
}

/**
 * Finds a Hubspot contact by email and updates their profile with the contact ID
 * This is a helper function for migration cases
 * @param email The email address to search for
 * @param userId The user ID to update with the Hubspot contact ID
 */
export async function findAndLinkHubspotContact(
  email: string,
  userId: string
): Promise<void> {
  // This would require additional implementation
  // We would need to add a "search" action to our Edge Function to find contacts by email
} 